import React, { useState } from 'react';
import { Input } from '../../ui';

interface AiAnalyzeImageConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
  onBatchChange?: (changes: Record<string, any>) => void;
}

const KNOWN_MODELS = new Set<string>([
  'claude-haiku-4-5',
  'claude-sonnet-5',
  'claude-opus-5-5',
  'claude-fable-5-1',
  'gpt-6-luna',
  'gpt-6-sol',
  'gpt-6-astra',
  'gpt-5',
  'gpt-5-mini',
  'gpt-4.1-mini',
  'gpt-4.1',
  'gpt-4o-mini',
  'gpt-4o',
  'gemini-3.8-flash',
  'gemini-3.5-flash-lite',
  'gemini-2.5-pro',
  'gemini-2.5-flash',
  'gemini-2.5-flash-lite',
  'gemini-2.0-flash',
  'gemini-2.0-flash-lite',
]);

export const AiAnalyzeImageConfig: React.FC<AiAnalyzeImageConfigProps> = ({ config, onChange, onBatchChange }) => {
  const provider = config.provider || 'openai';
  const [customModel, setCustomModel] = useState(false);
  const isCustomModel = customModel || (!!config.model && !KNOWN_MODELS.has(config.model));


  const handleProviderChange = (newProvider: string) => {
    const defaultModel =
      newProvider === 'gemini' ? 'gemini-2.0-flash'
      : newProvider === 'claude' ? 'claude-haiku-4-5'
      : 'gpt-6-luna';
    if (onBatchChange) {
      onBatchChange({ provider: newProvider, model: defaultModel });
    } else {
      onChange('provider', newProvider);
      onChange('model', defaultModel);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 leading-relaxed">
        <strong>Requisito obrigatório:</strong> este nó só funciona com conexão de IA ativa no aplicativo.
        Configure a chave em <strong>Configurações → Integrações</strong> (OpenAI ou Gemini).
      </div>

      <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3 text-xs text-fuchsia-700 leading-relaxed">
        <strong>🖼️ Análise de imagem:</strong> interpreta uma imagem usando IA.
        <br /><br />
        <strong>Entrada:</strong> usa automaticamente a imagem recebida do cliente (quando houver) ou URL configurada.
        <br />
        <strong>Saída:</strong> salva o resultado em <strong>image_analysis</strong> para os próximos nós.
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
        <div>
          <p className="text-xs font-medium text-slate-700">Variável de entrada automática</p>
          <p className="text-xs text-slate-500 mt-0.5">Não precisa configurar: o sistema preenche quando o cliente envia imagem.</p>
          <p className="mt-1 text-xs font-mono text-slate-800">image_file_url</p>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-700">Variável de saída</p>
          <p className="mt-1 text-xs font-mono text-slate-800">image_analysis</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Provedor de IA</label>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => handleProviderChange('openai')}
            className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
              provider === 'openai'
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <img src="/providers/openai.svg" alt="OpenAI" className="h-5 w-auto" />
          </button>
          <button
            type="button"
            onClick={() => handleProviderChange('gemini')}
            className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
              provider === 'gemini'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <img src="/providers/gemini.svg" alt="Google Gemini" className="h-5 w-auto" />
          </button>
          <button
            type="button"
            onClick={() => handleProviderChange('claude')}
            className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
              provider === 'claude'
                ? 'border-orange-500 bg-orange-50 text-orange-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <img src="/providers/claude.svg" alt="Claude" className="h-5 w-auto" />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
        <select
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={isCustomModel ? '__custom__' : (config.model || (provider === 'gemini' ? 'gemini-2.0-flash' : provider === 'claude' ? 'claude-haiku-4-5' : 'gpt-6-luna'))}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '__custom__') {
              setCustomModel(true);
              return;
            }
            setCustomModel(false);
            onChange('model', value);
          }}
        >
          {provider === 'claude' ? (
            <>
              <optgroup label="⭐ Recomendados">
                <option value="claude-haiku-4-5">Claude Haiku 4.5 — rápido e econômico ✓ recomendado</option>
                <option value="claude-sonnet-5">Claude Sonnet 5 — equilibrado</option>
              </optgroup>
              <optgroup label="Máxima qualidade">
                <option value="claude-opus-5-5">Claude Opus 5.5 — leitura mais detalhada</option>
                <option value="claude-fable-5-1">Claude Fable 5.1 — análise mais exigente</option>
              </optgroup>
            </>
          ) : provider === 'openai' ? (
            <>
              <optgroup label="⭐ GPT-6 (mais recentes)">
                <option value="gpt-6-luna">GPT-6 Luna — rápido e barato ✓ recomendado</option>
                <option value="gpt-6-sol">GPT-6 Sol — equilibrado</option>
                <option value="gpt-6-astra">GPT-6 Astra — máxima qualidade de visão</option>
              </optgroup>
              <optgroup label="GPT-5">
                <option value="gpt-5">GPT-5 — máxima qualidade de visão</option>
                <option value="gpt-5-mini">GPT-5 Mini — rápido e inteligente</option>
              </optgroup>
              <optgroup label="GPT-4.1 (Custo-benefício)">
                <option value="gpt-4.1-mini">GPT-4.1 Mini — equilibrado</option>
                <option value="gpt-4.1">GPT-4.1 — mais preciso</option>
              </optgroup>
              <optgroup label="GPT-4o (Geração anterior)">
                <option value="gpt-4o-mini">GPT-4o Mini — rápido e acessível</option>
                <option value="gpt-4o">GPT-4o — flexível</option>
              </optgroup>
            </>
          ) : (
            <>
              <optgroup label="⭐ Gemini 3 (mais recentes)">
                <option value="gemini-3.8-flash">Gemini 3.8 Flash — mais recente</option>
                <option value="gemini-3.5-flash-lite">Gemini 3.5 Flash Lite — o mais barato</option>
              </optgroup>
              <optgroup label="Gemini 2.5">
                <option value="gemini-2.5-pro">Gemini 2.5 Pro — máxima qualidade</option>
                <option value="gemini-2.5-flash">Gemini 2.5 Flash — rápido</option>
                <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite — o mais barato da 2.5</option>
              </optgroup>
              <optgroup label="Gemini 2.0 (Custo-benefício)">
                <option value="gemini-2.0-flash">Gemini 2.0 Flash — equilibrado</option>
                <option value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite — mais barato</option>
              </optgroup>
            </>
          )}
          <option value="__custom__">✏️ Outro modelo — digitar o ID…</option>
        </select>
        {isCustomModel && (
          <input
            type="text"
            className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="ID exato do modelo (ex.: gpt-5.3-luna)"
            value={config.model || ''}
            onChange={(e) => onChange('model', e.target.value.trim())}
            autoFocus
          />
        )}
      </div>

      <Input
        label="URL da imagem (opcional)"
        placeholder="https://.../imagem.jpg"
        value={config.imageUrl || ''}
        onChange={(e) => onChange('imageUrl', e.target.value)}
      />

      <Input
        label="Variável da URL da imagem"
        placeholder="Ex: image_file_url"
        value={config.imageUrlVariable || ''}
        onChange={(e) => onChange('imageUrlVariable', e.target.value.replace(/\s/g, '_'))}
      />

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Prompt</label>
        <textarea
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="Ex: Descreva os objetos e o contexto da imagem"
          value={config.prompt || ''}
          onChange={(e) => onChange('prompt', e.target.value)}
        />
      </div>

      <Input
        label="Salvar análise em"
        placeholder="Ex: image_analysis"
        value={config.responseVariable ?? 'image_analysis'}
        onChange={(e) => onChange('responseVariable', e.target.value.replace(/\s/g, '_'))}
      />
    </div>
  );
};
